import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Bell,
  Calendar,
  CheckCircle2,
  Clock,
  Edit,
  Eye,
  FlaskConical,
  Loader2,
  Pause,
  Pin,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  UserMinus,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatDateTime } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { SurveyPreviewDialog } from "./survey-preview-dialog";

interface AdminSurveyDetailViewProps {
  survey: Record<string, any>;
}

export function AdminSurveyDetailView({ survey }: AdminSurveyDetailViewProps) {
  const [, setLocation] = useLocation();
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [nextInstanceDateInput, setNextInstanceDateInput] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cancelInstanceId, setCancelInstanceId] = useState<string | null>(null);
  const [notifyInstanceId, setNotifyInstanceId] = useState<string | null>(null);
  const [deleteInstanceId, setDeleteInstanceId] = useState<string | null>(null);
  const [reopenInstanceId, setReopenInstanceId] = useState<string | null>(null);
  const [reopenSendNotifications, setReopenSendNotifications] = useState(true);
  const [exclusionSearchTerm, setExclusionSearchTerm] = useState("");
  const { toast } = useToast();

  const instances = survey.instances || [];

  const invalidateSurveyQueries = () => {
    queryClient.invalidateQueries({ queryKey: orpc.pulseSurveys.list.queryOptions().queryKey });
    queryClient.invalidateQueries({
      queryKey: orpc.pulseSurveys.getById.queryOptions({ input: { id: survey.id } }).queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: orpc.pulseSurveys.getDetail.queryOptions({ input: { id: survey.id } }).queryKey,
    });
  };

  const { data: exclusions = [], refetch: refetchExclusions } = useQuery(
    orpc.pulseSurveys.listExclusions.queryOptions({ input: { id: survey.id } }),
  );

  // Survey-scoped and gated by the survey capability: `employees.list` needs the
  // `employees` key, so it hands an Operador de Pesquisas an empty picker. The
  // endpoint already excludes who is on the list and who can't participate.
  const { data: eligibleEmployees = [], refetch: refetchExclusionCandidates } = useQuery(
    orpc.pulseSurveys.listExclusionCandidates.queryOptions({ input: { id: survey.id } }),
  );

  const filteredEligibleForAdd = eligibleEmployees.filter((e) =>
    exclusionSearchTerm.trim()
      ? e.fullName.toLowerCase().includes(exclusionSearchTerm.toLowerCase())
      : true,
  );

  const addExclusion = useMutation({
    mutationFn: async (employeeId: string) => {
      return rpcClient.pulseSurveys.addExclusion({ id: survey.id, employeeId });
    },
    onSuccess: () => {
      refetchExclusions();
      refetchExclusionCandidates();
      setExclusionSearchTerm("");
      toast({ title: "Colaborador excluído da pesquisa" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao excluir colaborador",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const removeExclusion = useMutation({
    mutationFn: async (employeeId: string) => {
      return rpcClient.pulseSurveys.removeExclusion({ id: survey.id, employeeId });
    },
    onSuccess: () => {
      refetchExclusions();
      refetchExclusionCandidates();
      toast({ title: "Colaborador incluído na pesquisa" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao incluir colaborador",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const sendSurvey = useMutation({
    mutationFn: async () => {
      return rpcClient.pulseSurveys.createInstance({ id: survey.id });
    },
    onSuccess: () => {
      invalidateSurveyQueries();
      setConfirmSendOpen(false);
      toast({ title: "Instancia criada com sucesso" });
    },
    onError: (error: Error) => {
      setConfirmSendOpen(false);
      toast({
        title: "Erro ao criar instancia",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const notifyInstance = useMutation({
    mutationFn: async (instanceId: string) => {
      return rpcClient.pulseSurveys.notifyInstance({ id: instanceId });
    },
    onSuccess: () => {
      invalidateSurveyQueries();
      setNotifyInstanceId(null);
      toast({ title: "Colaboradores notificados com sucesso" });
    },
    onError: (error: Error) => {
      setNotifyInstanceId(null);
      toast({
        title: "Erro ao notificar colaboradores",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const hasActiveInstance = instances.some((i) => i.status === "active");
  const hasDraftInstance = instances.some((i) => i.status === "draft");

  const handleSendSurvey = () => {
    setConfirmSendOpen(true);
  };

  const toggleActive = useMutation({
    mutationFn: async (_payload: { nextInstanceDate?: string }) => {
      return rpcClient.pulseSurveys.toggleActive({ id: survey.id });
    },
    onSuccess: () => {
      invalidateSurveyQueries();
      setActivateDialogOpen(false);
      setNextInstanceDateInput("");
      toast({ title: survey.isActive ? "Pesquisa pausada" : "Pesquisa ativada" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar pesquisa",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cancelInstance = useMutation({
    mutationFn: async (instanceId: string) => {
      return rpcClient.pulseSurveys.cancelInstance({ id: instanceId });
    },
    onSuccess: () => {
      invalidateSurveyQueries();
      setCancelInstanceId(null);
      toast({ title: "Instancia cancelada com sucesso" });
    },
    onError: (error: Error) => {
      setCancelInstanceId(null);
      toast({
        title: "Erro ao cancelar instancia",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteInstance = useMutation({
    mutationFn: async (instanceId: string) => {
      return rpcClient.pulseSurveys.deleteInstance({ id: instanceId });
    },
    onSuccess: () => {
      invalidateSurveyQueries();
      setDeleteInstanceId(null);
      toast({ title: "Instancia deletada com sucesso" });
    },
    onError: (error: Error) => {
      setDeleteInstanceId(null);
      toast({
        title: "Erro ao deletar instancia",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const reopenInstance = useMutation({
    mutationFn: async ({ instanceId }: { instanceId: string; sendNotifications: boolean }) => {
      return rpcClient.pulseSurveys.reopenInstance({ id: instanceId });
    },
    onSuccess: (_data, { sendNotifications }) => {
      invalidateSurveyQueries();
      setReopenInstanceId(null);
      toast({
        title: "Instância reaberta com sucesso",
        description: sendNotifications
          ? "Lembretes enviados para os não-respondentes."
          : "Prazo estendido até 23:59 de hoje.",
      });
    },
    onError: (error: Error) => {
      setReopenInstanceId(null);
      toast({
        title: "Erro ao reabrir instância",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleToggleActive = () => {
    if (survey.isActive) {
      toggleActive.mutate({});
    } else {
      setActivateDialogOpen(true);
    }
  };

  const handleActivateConfirm = () => {
    toggleActive.mutate({
      nextInstanceDate: nextInstanceDateInput || undefined,
    });
  };

  const getSurveyTypeBadge = (type: string) => {
    switch (type) {
      case "recurring":
        return (
          <Badge variant="secondary" className="gap-1">
            <RefreshCw className="h-3 w-3" />
            Recorrente
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1">
            <Pin className="h-3 w-3" />
            Pontual
          </Badge>
        );
    }
  };

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title={survey.title}
        description={survey.description || "Pesquisa de clima organizacional"}
        onBack={() => setLocation("/surveys")}
        titleExtra={
          <div className="flex items-center gap-2 flex-wrap">
            {getSurveyTypeBadge(survey.surveyType)}
            {survey.surveyType === "recurring" &&
              (survey.isActive ? (
                <StatusBadge status="active" />
              ) : (
                <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--amber-state))]">
                  <Pause className="h-3 w-3" /> Pausada
                </span>
              ))}
          </div>
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={() => setLocation(`/surveys/${survey.id}/edit`)}
          data-testid="button-edit-survey"
        >
          <Edit className="h-4 w-4 mr-2" />
          Editar Pesquisa
        </Button>
        <Button
          variant="outline"
          onClick={() => setPreviewOpen(true)}
          data-testid="button-preview-survey"
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar
        </Button>
        {(survey.surveyType === "adhoc" || survey.surveyType === "recurring") && (
          <Button
            onClick={handleSendSurvey}
            disabled={sendSurvey.isPending}
            data-testid="button-create-instance"
          >
            <Plus className="h-4 w-4 mr-2" />
            Criar Instância
          </Button>
        )}
        {survey.surveyType === "recurring" && (
          <Button
            variant="outline"
            onClick={handleToggleActive}
            disabled={toggleActive.isPending}
            data-testid="button-toggle-active"
          >
            {toggleActive.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : survey.isActive ? (
              <Pause className="h-4 w-4 mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {survey.isActive ? "Pausar" : "Ativar"}
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Plus className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {instances.length}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Instâncias</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--forest-pulse))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {
                  instances.filter((i) => i.status === "finished" || i.status === "completed")
                    .length
                }
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Finalizadas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Clock className="h-5 w-5 text-[hsl(var(--amber-state))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {instances.filter((i) => i.status === "active" || i.status === "pending").length}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Em andamento</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {instances && instances.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Instancias</CardTitle>
                <CardDescription>
                  {instances.length} instancia{instances.length > 1 ? "s" : ""}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {instances.map((instance: any) => (
                  // biome-ignore lint/a11y/useSemanticElements: interactive container with nested interactive elements cannot be a native button
                  <div
                    key={instance.id}
                    className="flex items-center justify-between gap-3 p-3 rounded-md cursor-pointer transition-colors hover:bg-[hsl(var(--sand-step))]"
                    role="button"
                    tabIndex={0}
                    onClick={() => setLocation(`/surveys/instances/${instance.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setLocation(`/surveys/instances/${instance.id}`);
                      }
                    }}
                    data-testid={`instance-row-${instance.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Calendar className="h-4 w-4 text-[hsl(var(--ink-muted))] shrink-0" />
                        <span className="text-sm font-medium text-[hsl(var(--ink-primary))]">
                          {instance.status === "draft" ? "Criado em" : "Enviado em"}{" "}
                          {formatDateTime(instance.sentAt)}
                        </span>
                        <StatusBadge status={instance.status} />
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-xs text-[hsl(var(--ink-muted))]">
                          Prazo: {formatDate(instance.deadline)}
                        </p>
                        {instance.totalResponses !== undefined && (
                          <span className="text-xs text-[hsl(var(--ink-muted))] tabular-nums">
                            {instance.finishedResponses}/{instance.totalResponses} responderam (
                            {instance.responseRate}%)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {instance.status === "draft" && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocation(`/my-surveys/${instance.id}?test=true`);
                            }}
                            data-testid={`button-test-instance-${instance.id}`}
                          >
                            <FlaskConical className="h-4 w-4 mr-1" />
                            Testar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[hsl(var(--forest-pulse))]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNotifyInstanceId(instance.id);
                            }}
                            data-testid={`button-notify-instance-${instance.id}`}
                          >
                            <Bell className="h-4 w-4 mr-1" />
                            Notificar
                          </Button>
                        </>
                      )}
                      {instance.status === "active" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[hsl(var(--alert-red))]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCancelInstanceId(instance.id);
                          }}
                          data-testid={`button-cancel-instance-${instance.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancelar
                        </Button>
                      )}
                      {instance.status === "completed" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[hsl(var(--forest-pulse))]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReopenSendNotifications(true);
                            setReopenInstanceId(instance.id);
                          }}
                          data-testid={`button-reopen-instance-${instance.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Reabrir
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLocation(`/surveys/instances/${instance.id}`);
                        }}
                      >
                        {survey.canViewResponses === false ? "Ver Detalhes" : "Ver Respostas"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[hsl(var(--alert-red))]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteInstanceId(instance.id);
                        }}
                        data-testid={`button-delete-instance-${instance.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informacoes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {survey.surveyType === "recurring" && (
                <>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Proximo Envio</p>
                    <p className="text-sm">
                      {survey.nextInstanceDate
                        ? formatDateTime(survey.nextInstanceDate)
                        : "Nao agendado"}
                    </p>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Recorrencia</p>
                    <p className="text-sm">A cada {survey.recurrenceInDays} dias</p>
                  </div>
                  <Separator />
                </>
              )}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  Prazo para Resposta
                </p>
                <p className="text-sm">{survey.deadlineInDays} dias</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Perguntas</p>
                <p className="text-sm">{survey.questions?.length || 0} perguntas</p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-pulse-exclusions">
            <CardHeader>
              <CardTitle className="text-lg">Exclusões</CardTitle>
              <CardDescription>
                Colaboradores excluídos não receberão esta pesquisa em novos envios.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {exclusions.length > 0 && (
                <div className="space-y-2">
                  {exclusions.map((excl) => (
                    <div
                      key={excl.id}
                      className="flex items-center justify-between gap-2 p-2 bg-[hsl(var(--paper-2))] rounded-md"
                    >
                      <span className="text-sm truncate">
                        {excl.employee?.fullName ?? excl.employeeId}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-[hsl(var(--forest-pulse))] shrink-0"
                        onClick={() => removeExclusion.mutate(excl.employeeId)}
                        disabled={removeExclusion.isPending}
                        title="Remover exclusão"
                        data-testid={`button-remove-exclusion-${excl.employeeId}`}
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Separator />
                </div>
              )}

              <div className="space-y-2">
                <Input
                  placeholder="Buscar colaborador para excluir…"
                  value={exclusionSearchTerm}
                  onChange={(e) => setExclusionSearchTerm(e.target.value)}
                  data-testid="input-exclusion-search"
                />
                {exclusionSearchTerm.trim() && (
                  <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-1">
                    {filteredEligibleForAdd.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">
                        Nenhum colaborador encontrado.
                      </p>
                    ) : (
                      filteredEligibleForAdd.slice(0, 20).map((emp) => (
                        <button
                          key={emp.id}
                          type="button"
                          className="flex items-center justify-between w-full gap-2 px-2 py-1.5 text-sm hover:bg-[hsl(var(--sand-step))] rounded cursor-pointer text-left"
                          onClick={() => addExclusion.mutate(emp.id)}
                          disabled={addExclusion.isPending}
                          data-testid={`button-add-exclusion-${emp.id}`}
                        >
                          <span className="truncate">{emp.fullName}</span>
                          <UserMinus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Nova Instância</DialogTitle>
            <DialogDescription>
              {hasActiveInstance || hasDraftInstance
                ? "Já existe uma instância ativa ou em rascunho dessa pesquisa. Tem certeza que deseja criar uma nova?"
                : "Deseja criar uma nova instância desta pesquisa? Ela será criada como rascunho para que você possa testá-la antes de notificar os colaboradores."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmSendOpen(false)}
              data-testid="button-cancel-send"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => sendSurvey.mutate()}
              disabled={sendSurvey.isPending}
              data-testid="button-confirm-send"
            >
              {sendSurvey.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {hasActiveInstance || hasDraftInstance ? "Criar Mesmo Assim" : "Criar Instância"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={activateDialogOpen} onOpenChange={setActivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ativar Pesquisa Recorrente</DialogTitle>
            <DialogDescription>
              Selecione a data do proximo envio para ativar a pesquisa.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="datetime-local"
              value={nextInstanceDateInput}
              onChange={(e) => setNextInstanceDateInput(e.target.value)}
              data-testid="input-next-instance-date"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setActivateDialogOpen(false)}
              data-testid="button-cancel-activate"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleActivateConfirm}
              disabled={toggleActive.isPending}
              data-testid="button-confirm-activate"
            >
              {toggleActive.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Ativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!cancelInstanceId}
        onOpenChange={(open) => {
          if (!open) setCancelInstanceId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Instancia</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja cancelar esta instancia? Os colaboradores nao poderao mais
              responder.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelInstanceId(null)}
              data-testid="button-cancel-cancel-instance"
            >
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (cancelInstanceId) cancelInstance.mutate(cancelInstanceId);
              }}
              disabled={cancelInstance.isPending}
              data-testid="button-confirm-cancel-instance"
            >
              {cancelInstance.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Cancelar Instancia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!notifyInstanceId}
        onOpenChange={(open) => {
          if (!open) setNotifyInstanceId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notificar Colaboradores</DialogTitle>
            <DialogDescription>
              Deseja enviar notificações para todos os colaboradores desta instância? A instância
              será ativada e os colaboradores serão notificados via Slack.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setNotifyInstanceId(null)}
              data-testid="button-cancel-notify"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (notifyInstanceId) notifyInstance.mutate(notifyInstanceId);
              }}
              disabled={notifyInstance.isPending}
              data-testid="button-confirm-notify"
            >
              {notifyInstance.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Notificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteInstanceId}
        onOpenChange={(open) => {
          if (!open) setDeleteInstanceId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deletar Instancia</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja deletar esta instancia? Todas as respostas e dados associados
              serao permanentemente removidos. Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteInstanceId(null)}
              data-testid="button-cancel-delete-instance"
            >
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteInstanceId) deleteInstance.mutate(deleteInstanceId);
              }}
              disabled={deleteInstance.isPending}
              data-testid="button-confirm-delete-instance"
            >
              {deleteInstance.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Deletar Instancia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!reopenInstanceId}
        onOpenChange={(open) => {
          if (!open) setReopenInstanceId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reabrir Instância</DialogTitle>
            <DialogDescription>
              A instância será reaberta com prazo até 23:59 de hoje.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="reopen-notify"
                checked={reopenSendNotifications}
                onCheckedChange={(checked) => setReopenSendNotifications(!!checked)}
                data-testid="checkbox-reopen-notify"
              />
              <Label htmlFor="reopen-notify" className="cursor-pointer">
                Notificar não-respondentes via Slack
              </Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReopenInstanceId(null)}
              data-testid="button-cancel-reopen"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (reopenInstanceId)
                  reopenInstance.mutate({
                    instanceId: reopenInstanceId,
                    sendNotifications: reopenSendNotifications,
                  });
              }}
              disabled={reopenInstance.isPending}
              data-testid="button-confirm-reopen"
            >
              {reopenInstance.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4 mr-2" />
              )}
              Reabrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SurveyPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title={survey.title}
        description={survey.description}
        questions={survey.questions || []}
      />
    </div>
  );
}
